/**
 * Client mirror of the server's document rules (server/routes/documents.js), so the UI
 * never offers an upload, a review or a delete the server refuses. The server decides on
 * the exact grant:
 * - upload: into the caller's own folder with documents:create or create_own; into anyone
 *   else's only with documents:update_all as well (HR managing employees' documents);
 * - approve / reject: documents:approve, a pending document only, never the caller's own;
 * - delete: documents:delete_all reaches any document, delete_own only one whose owner_id is
 *   the caller's user id (documents are owned by the subject employee's user);
 * - withdraw: the owner who may upload (documents:create / create_own) may delete their own
 *   document while it is still pending (canWithdrawDocument).
 *
 * Each helper takes AuthContext's `hasPermission(resource, action)`.
 */

// A null on either side never matches, unlike String(null) === String(null).
const same = (a, b) => a != null && b != null && String(a) === String(b);

const isOwner = (user, doc) => same(doc?.owner_id, user?.id);

/** @returns {boolean} whether the caller may upload into `employeeId`'s documents folder. */
export const canUploadDocumentsFor = (hasPermission, user, employeeId) => {
  const mayCreate = hasPermission('documents', 'create') || hasPermission('documents', 'create_own');
  if (!mayCreate) return false;
  return same(employeeId, user?.employee_id) || hasPermission('documents', 'update_all');
};

/** @returns {boolean} whether the caller may approve or reject this document now. */
export const canReviewDocument = (hasPermission, user, doc) => Boolean(doc)
  && doc.status === 'pending'
  && hasPermission('documents', 'approve')
  && !isOwner(user, doc);

/** @returns {boolean} whether the caller may delete this document outright. */
export const canDeleteDocument = (hasPermission, user, doc) => {
  if (!doc) return false;
  if (hasPermission('documents', 'delete_all')) return true;
  return hasPermission('documents', 'delete_own') && isOwner(user, doc);
};

/** @returns {boolean} whether the caller may withdraw (delete) their own pending document. */
export const canWithdrawDocument = (hasPermission, user, doc) => Boolean(doc)
  && doc.status === 'pending'
  && isOwner(user, doc)
  && (hasPermission('documents', 'create') || hasPermission('documents', 'create_own'));
